// TIDAK TERPAKAI — halaman V1, tidak terjangkau sejak App.js mengarahkan semua rute ke V2App. Jangan diedit. Lihat memory/DEPRECATED.md.
// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE
// version  : 1.1
// updated  : 2026-06-17
// replaces : -
// protected: NO  -> Tambahkan riwayat di /app/memory CHANGELOG saat diubah.
// changelog: v1.1 - saveRole bisa MENAIKKAN user PENDING (yang sudah login) ke
//            role via PUT /users/{id}, tidak lagi tertolak "Email sudah dipakai".
// =============================================================================

import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, Loader2, ShieldCheck, Mail, UserCog, Lock } from "lucide-react";
import { toast } from "sonner";

// Roles grouped per region. Each role has exactly 2 email slots.
const GROUPS = [
  { title: "Global", roles: ["OWNER"] },
  { title: "Region Surabaya (Arema)", roles: ["KACAB_SBY", "ADMIN_PRODUKSI_SBY", "ADMIN_GUDANG_SBY", "KEPALA_STORE_AREMA"] },
  { title: "Region Samarinda (Borneo)", roles: ["KACAB_SMD", "ADMIN_PRODUKSI_SMD", "ADMIN_GUDANG_SMD", "KEPALA_STORE_BORNEO"] },
];
const ALL_ROLES = GROUPS.flatMap((g) => g.roles);
const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

export default function UserManagement() {
  const { meta, user } = useAuth();
  const myEmail = (user?.email || "").toLowerCase();
  const [users, setUsers] = useState([]);
  const [occByRole, setOccByRole] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savingRole, setSavingRole] = useState(null);

  const label = useCallback((r) => meta?.role_labels?.[r] || r, [meta]);

  const load = useCallback(async () => {
    try {
      const list = (await api.get("/users")).data;
      setUsers(list);
      const by = {};
      ALL_ROLES.forEach((r) => { by[r] = []; });
      list.forEach((u) => { if (by[u.role]) by[u.role].push(u); });
      // keep the logged-in owner pinned to slot 0 for the OWNER role
      if (by.OWNER) {
        by.OWNER.sort((a, b) => (a.email === myEmail ? -1 : b.email === myEmail ? 1 : 0));
      }
      setOccByRole(by);
      const d = {};
      ALL_ROLES.forEach((r) => { d[r] = [by[r][0]?.email || "", by[r][1]?.email || ""]; });
      setDrafts(d);
    } catch (e) { console.error(e); toast.error("Gagal memuat daftar user"); }
  }, [myEmail]);
  useEffect(() => { load(); }, [load]);

  const setDraft = useCallback((role, i, val) => {
    setDrafts((prev) => {
      const arr = [...(prev[role] || ["", ""])];
      arr[i] = val;
      return { ...prev, [role]: arr };
    });
  }, []);

  const saveRole = async (role) => {
    setSavingRole(role);
    let changed = 0;
    try {
      const occ = occByRole[role] || [];
      for (let i = 0; i < 2; i++) {
        const raw = (drafts[role]?.[i] || "").trim().toLowerCase();
        const cur = occ[i];
        // never touch the logged-in owner's own account
        if (cur && cur.email === myEmail) continue;

        if (cur) {
          if (!raw) {
            await api.delete(`/users/${cur.id}`); changed++;            // dikosongkan -> hapus
          } else if (raw !== cur.email) {
            if (!validEmail(raw)) { toast.error(`Format email tidak valid: ${raw}`); continue; }
            if (users.some((u) => u.email === raw && u.id !== cur.id)) { toast.error(`Email ${raw} sudah dipakai`); continue; }
            await api.put(`/users/${cur.id}`, { email: raw }); changed++; // ganti email
          }
        } else if (raw) {
          if (!validEmail(raw)) { toast.error(`Format email tidak valid: ${raw}`); continue; }
          const existing = users.find((u) => u.email === raw);
          if (existing) {
            // User sudah pernah login (mis. status PENDING). Naikkan role-nya
            // langsung, jangan tolak. Hanya tolak jika sudah punya role lain.
            if (existing.role === "PENDING" || existing.role === role) {
              await api.put(`/users/${existing.id}`, { role, active: true }); changed++;
            } else {
              toast.error(`Email ${raw} sudah dipakai di role ${label(existing.role)}`); continue;
            }
          } else {
            await api.post("/users", { email: raw, role }); changed++;    // email baru
          }
        }
      }
      if (changed > 0) { toast.success(`${label(role)} disimpan`); }
      else toast.info("Tidak ada perubahan");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); await load(); }
    finally { setSavingRole(null); }
  };

  return (
    <div>
      <PageHeader title="User Management" subtitle="Tetapkan email untuk setiap role. Maksimal 2 email per role." />

      <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted rounded-xl p-3 mb-5">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
        <span>Ketik alamat email Google pada kotak untuk <b>menambah</b>, atau <b>kosongkan</b> kotak untuk <b>menghapus</b>, lalu klik <b>Simpan</b>. User otomatis mendapat role tersebut saat login.</span>
      </div>

      {GROUPS.map((g) => (
        <div key={g.title} className="mb-6">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">{g.title}</h3>
          <div className="space-y-3">
            {g.roles.map((role) => {
              const occ0 = (occByRole[role] || [])[0];
              const occ1 = (occByRole[role] || [])[1];
              const lock0 = occ0 && occ0.email === myEmail; // owner's own account
              return (
                <Card key={role} className="p-4 rounded-2xl kpi-shadow" data-testid={`role-card-${role}`}>
                  <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr_auto] gap-3 lg:items-center">
                    <div className="font-medium flex items-center gap-2">
                      <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <UserCog className="w-4 h-4 text-primary" />
                      </span>
                      {label(role)}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {/* Slot 0 */}
                      <div className="relative">
                        {lock0 ? <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                               : <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
                        <Input type="email" className="pl-9" placeholder="Email 1"
                          value={drafts[role]?.[0] ?? ""}
                          disabled={lock0}
                          title={lock0 ? "Akun Owner Anda (terkunci)" : undefined}
                          onChange={(e) => setDraft(role, 0, e.target.value)}
                          data-testid={`role-input-${role}-0`} />
                      </div>
                      {/* Slot 1 */}
                      <div className="relative">
                        <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input type="email" className="pl-9" placeholder="Email 2 (opsional)"
                          value={drafts[role]?.[1] ?? ""}
                          onChange={(e) => setDraft(role, 1, e.target.value)}
                          data-testid={`role-input-${role}-1`} />
                      </div>
                    </div>
                    <Button onClick={() => saveRole(role)} disabled={savingRole === role} className="lg:w-auto w-full" data-testid={`role-save-${role}`}>
                      {savingRole === role ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Simpan
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
